import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Saúde da conexão Evolution para uma conversa.
 *
 * Combina 2 sinais (recomendação da comunidade Baileys/Evolution):
 *
 *  1. `connectionState` snapshot via API (polling 45s) com HISTERESE
 *     — só vira isDown após 3× close consecutivos. Evita flicker do
 *     state oscilando entre open/connecting/close.
 *
 *  2. Contagem de `connection.update` no `webhook_logs` no último minuto.
 *     >30 mudanças/min é sinal claro de loop reason 428 (bug conhecido
 *     do Evolution v2 — issue EvolutionAPI/evolution-api#693). Nesse
 *     caso o restart NÃO resolve; só logout + novo QR ou delete+create.
 *
 * Retorna 2 booleanos pra UI decidir a mensagem certa:
 *  - `isDown`     → state estável em close (instância caiu, pode tentar restart)
 *  - `isLooping`  → state oscilando rápido (>30 conn.updates/min, recomendar logout+QR)
 *
 * Refs:
 *  - https://baileys.wiki/docs/api/enumerations/DisconnectReason/
 *  - https://github.com/EvolutionAPI/evolution-api/issues/693
 *  - https://github.com/WhiskeySockets/Baileys/issues/2249
 */

export type EvolutionState = 'open' | 'connecting' | 'close' | 'unknown';

export interface EvolutionHealth {
  provider: 'evolution' | 'evolution_go' | '360dialog' | 'bee' | null;
  instanceName: string | null;
  rawState: EvolutionState;
  /** Conexão estavelmente caída. Restart pode resolver. */
  isDown: boolean;
  /** Conexão em loop (>30 connection.update/min). Restart NÃO resolve, precisa logout+QR. */
  isLooping: boolean;
  /** Quantos connection.update no último minuto — pra mostrar no banner. */
  loopCount: number;
  refresh: () => void;
}

const POLL_MS = 45_000;
const LOOP_POLL_MS = 60_000;
const CLOSE_THRESHOLD = 3;
const OPEN_THRESHOLD = 2;
const LOOP_THRESHOLD = 30; // > 30 connection.update/min = loop

function mapState(raw: string | null | undefined): EvolutionState {
  if (raw === 'open') return 'open';
  if (raw === 'connecting') return 'connecting';
  if (raw === 'close') return 'close';
  return 'unknown';
}

export function useEvolutionHealth(connectionId: string | null | undefined): EvolutionHealth {
  const qc = useQueryClient();

  // Identifica provider + instance
  const { data: conn } = useQuery({
    queryKey: ['evolution-health-conn', connectionId],
    enabled: !!connectionId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('whatsapp_connections')
        .select('provider, provider_instance_id, ativo')
        .eq('id', connectionId as string)
        .maybeSingle();
      if (error) return null;
      return data;
    },
  });

  const provider = (conn?.provider as 'evolution' | 'evolution_go' | '360dialog' | 'bee' | undefined) ?? null;
  const instanceName = (conn?.provider_instance_id as string | undefined) ?? null;
  const isEvolutionActive = (provider === 'evolution' || provider === 'evolution_go') && !!instanceName && conn?.ativo === true;

  // Sinal 1: connectionState snapshot
  const { data: stateRaw, refetch: refetchState } = useQuery({
    queryKey: ['evolution-health-state', instanceName],
    enabled: isEvolutionActive,
    refetchInterval: POLL_MS,
    staleTime: POLL_MS / 2,
    queryFn: async () => {
      try {
        const { data } = await supabase.functions.invoke('evolution-instance-manage', {
          body: { action: 'connectionState', instance_name: instanceName },
        });
        return data?.data?.instance?.state || data?.data?.state || 'unknown';
      } catch {
        return 'unknown';
      }
    },
  });

  // Sinal 2: contagem de connection.update no último minuto (detecta loop).
  // RPC SECURITY DEFINER porque webhook_logs é service_role only — a RPC
  // só expõe o número agregado.
  const { data: loopCount = 0, refetch: refetchLoop } = useQuery({
    queryKey: ['evolution-health-loop', instanceName],
    enabled: isEvolutionActive,
    refetchInterval: LOOP_POLL_MS,
    staleTime: LOOP_POLL_MS / 2,
    queryFn: async () => {
      try {
        const { data, error } = await supabase.rpc('fn_evolution_loop_count', {
          p_instance_name: instanceName as string,
        });
        if (error) return 0;
        return (data as number) || 0;
      } catch {
        return 0;
      }
    },
  });

  const rawState = mapState(stateRaw as string | null | undefined);
  const isLooping = isEvolutionActive && loopCount > LOOP_THRESHOLD;

  // Histerese no isDown
  const [stableIsDown, setStableIsDown] = useState(false);
  const closeStreak = useRef(0);
  const openStreak = useRef(0);

  useEffect(() => {
    if (!isEvolutionActive) {
      closeStreak.current = 0;
      openStreak.current = 0;
      setStableIsDown(false);
      return;
    }
    if (rawState === 'close') {
      closeStreak.current += 1;
      openStreak.current = 0;
      if (closeStreak.current >= CLOSE_THRESHOLD && !stableIsDown) setStableIsDown(true);
    } else if (rawState === 'open') {
      openStreak.current += 1;
      closeStreak.current = 0;
      if (openStreak.current >= OPEN_THRESHOLD && stableIsDown) setStableIsDown(false);
    }
  }, [rawState, isEvolutionActive, stableIsDown]);

  return {
    provider,
    instanceName,
    rawState,
    isDown: stableIsDown,
    isLooping,
    loopCount,
    refresh: () => {
      // Após acao manual (restart, logout+QR), forcamos otimisticamente isDown=false.
      // O banner some na hora e o composer libera. Se a proxima leitura mostrar
      // close de novo, a histerese (3 amostras) volta a marcar isDown=true. Isso
      // da tempo (~2min) pro usuario mandar uma msg teste e ver se reconectou.
      closeStreak.current = 0;
      openStreak.current = 0;
      setStableIsDown(false);
      qc.invalidateQueries({ queryKey: ['evolution-health-state', instanceName] });
      qc.invalidateQueries({ queryKey: ['evolution-health-loop', instanceName] });
      qc.invalidateQueries({ queryKey: ['bee360-evolution-states'] });
      refetchState();
      refetchLoop();
    },
  };
}
